import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import z from 'zod';
import { privateHttpClient } from '../httpClient';
import { createApiResponseSchema } from '../utils';
import { curationsApiPath } from './apiPath';

const GetCurationRequestParamsSchema = z
  .object({
    curationId: z.string(),
  })
  .describe('GetCurationRequestParamsSchema');

type GetCurationRequestParams = z.infer<typeof GetCurationRequestParamsSchema>;

const CurationSnapItemSchema = z
  .object({
    id: z.string(),
    imageUrl: z.string(),
    linkUrl: z.string(),
  })
  .describe('CurationSnapItemSchema');

const CurationGoodsItemSchema = z
  .object({
    goodsNo: z.number(),
    imageUrl: z.string(),
    isLiked: z.boolean(),
    brandNo: z.number().nullish(),
    brandName: z.string(),
    goodsName: z.string(),
    salesRate: z.number(),
    price: z.number(),
    expectedEarnings: z.number(),
    likeCount: z.number(),
    reviewScore: z.string(),
    reviewCount: z.number(),
    isSoldOut: z.boolean(),
    ordNo: z.number(),
    extraDiscountAmount: z
      .number()
      .int()
      .nullish()
      .describe('추가할인 금액(원). 표시는 finalPrice 사용 (주문 도메인 전용, MUSINSA 전용)'),
    finalPrice: z
      .number()
      .int()
      .nullish()
      .describe('최종가 = 세일가 + 상품쿠폰 + 추가할인 (장바구니쿠폰 제외, MUSINSA 전용)'),
    finalDiscount: z
      .number()
      .int()
      .nullish()
      .describe('정상가 대비 finalPrice 할인율 (MUSINSA 전용)'),
    isLowestPrice: z
      .boolean()
      .nullish()
      .describe('RADAR 추가할인 최저가 보장 여부 — true 시 최저가도전 라벨 (MUSINSA 전용)'),
    isExclusive: z.boolean().nullish().describe('단독 상품 여부 (MUSINSA 전용)'),
    existsCoupon: z.boolean().nullish().describe('쿠폰 존재 여부'),
    largeCategoryNo: z.number().optional().nullable(),
    largeCategoryName: z.string().optional().nullable(),
    middleCategoryNo: z.number().optional().nullable(),
    middleCategoryName: z.string().optional().nullable(),
    smallCategoryNo: z.number().optional().nullable(),
    smallCategoryName: z.string().optional().nullable(),
  })
  .describe('CurationGoodsItemSchema');

/** OpenAPI `GetCurationDetailV2Response` — `type`은 GOODS/SNAP2 등 */
const GetCurationResponseDataSchema = z
  .object({
    curationId: z.string(),
    title: z.string(),
    description: z.string().nullish().default(''),
    type: z.string().nullish(),
    ordNo: z.number(),
    goodsItems: z.array(CurationGoodsItemSchema),
    snapItems: z.array(CurationSnapItemSchema).default([]),
  })
  .describe('GetCurationResponseDataSchema');

const GetCurationResponseSchema = createApiResponseSchema(GetCurationResponseDataSchema);
type GetCurationResponse = z.infer<typeof GetCurationResponseSchema>;

export const getCuration = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(GetCurationRequestParamsSchema),
    async (params: GetCurationRequestParams): Promise<GetCurationResponse> => {
      const response = await privateHttpClient.get<GetCurationResponse>(
        curationsApiPath.getCuration(params.curationId),
      );
      return response.data;
    },
    validateApiResponse(GetCurationResponseSchema),
  ),
);
